import React, { ReactElement, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import clsx from "clsx"
import { CSSTransition } from "react-transition-group"
import { Absolute, Box } from "ui/components/atoms/_atoms"
import Button, { IconButton } from "ui/components/atoms/button"
import { IconBox } from "ui/components/atoms/icon"
import { H2, P } from "ui/components/atoms/typography"
import { useBodyScrollLock } from "ui/hooks"

import styles from "./Modal.module.css"

const Portal = ({ id, children }: { id: string; children: ReactElement }) => {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)

    return () => setMounted(false)
  }, [])

  const target = mounted ? document.querySelector(`#${id}`) : null

  return mounted && target ? createPortal(children, target) : null
}

const Modal = ({
  children,
  show,
  setShow,
  type = "center",
  icon,
  title,
  text,
  titleElement,
  bottomElement,
  headerElement,
  helperElement,
  helperButton,
  showBackground = true,
  showTitleBorder,
  mobileFullScreen = false,
  modalBackgroundId = "root-modal-background",
  modalContentId = "root-modal-content",
  $maxWidth = "500px",
  $maxHeight,
  $iconColor,
  $iconBackground,
}: {
  children: ReactElement | ReactElement[]
  show: boolean
  setShow?: (e: boolean) => void
  type?: "center" | "side" | "bottom"
  icon?: string
  title?: string
  text?: string
  titleElement?: ReactElement
  headerElement?: ReactElement
  helperElement?: ReactElement
  bottomElement?: ReactElement
  helperButton?: {
    text: string
    color?: string
    onClick: () => void
  }
  showBackground?: boolean
  showTitleBorder?: boolean
  mobileFullScreen?: boolean
  modalBackgroundId?: string
  modalContentId?: string
  $maxWidth?: string
  $maxHeight?: string
  $iconColor?: string
  $iconBackground?: string
}) => {
  useBodyScrollLock(show)

  const backgroundRef = React.useRef(null)
  const contentRef = React.useRef(null)

  const modalChildProps = { setShow } as React.Attributes & {
    setShow?: (open: boolean) => void
  }

  let childrenElement: ReactElement | ReactElement[] | undefined
  if (children) {
    if (Array.isArray(children)) {
      childrenElement = children.map(el => React.cloneElement(el, modalChildProps))
    } else {
      childrenElement = React.cloneElement(children, modalChildProps)
    }
  }

  return (
    <>
      {showBackground && (
        <Portal id={modalBackgroundId}>
          <CSSTransition
            nodeRef={backgroundRef}
            in={show}
            timeout={200}
            classNames={{
              enter: styles.backgroundEnter,
              enterActive: styles.backgroundEnterActive,
              exit: styles.backgroundExit,
              exitActive: styles.backgroundExitActive,
            }}
            unmountOnExit
          >
            <div
              ref={backgroundRef}
              className={styles.background}
              onClick={setShow ? () => setShow(false) : () => {}}
            />
          </CSSTransition>
        </Portal>
      )}
      <Portal id={modalContentId}>
        <CSSTransition
          nodeRef={contentRef}
          in={show}
          timeout={200}
          classNames={{
            enter: styles[`${type}Enter`],
            enterActive: styles[`${type}EnterActive`],
            exit: styles[`${type}Exit`],
            exitActive: styles[`${type}ExitActive`],
          }}
          unmountOnExit
        >
          <>
            <div
              ref={contentRef}
              className={clsx(
                styles.wrapper,
                {
                  [styles.center]: type === "center",
                  [styles.side]: type === "side",
                  [styles.bottom]: type === "bottom",
                  [styles.mobileFullScreen]: mobileFullScreen,
                },
                "hide_scroll",
              )}
              style={{
                maxWidth: $maxWidth,
                maxHeight: $maxHeight,
              }}
            >
              {headerElement && <div className={styles.headerWrapper}>{headerElement}</div>}
              <div
                className={clsx(
                  styles.innerWrapper,
                  {
                    [styles.center]: type === "center",
                    [styles.side]: type === "side",
                    [styles.bottom]: type === "bottom",
                    [styles.mobileFullScreen]: mobileFullScreen,
                  },
                  "sidebar_block hide_scroll",
                )}
              >
                <div
                  className={clsx(
                    styles.innerContent,
                    {
                      [styles.center]: type === "center",
                    },
                    "sidebar_block hide_scroll",
                  )}
                >
                  {title && (
                    <div
                      className={clsx(styles.titleContent, {
                        [styles.showTitleBorder]: showTitleBorder ?? type === "side",
                      })}
                    >
                      <Box $width="100%">
                        {icon && (
                          <IconBox
                            icon={icon}
                            $color={$iconColor}
                            $background={$iconBackground}
                            $marginBottom="S"
                          />
                        )}
                        <H2 $marginBottom="0">{title}</H2>
                        {text && (
                          <P $small $marginBottom="0" $marginTop="XXS">
                            {text}
                          </P>
                        )}
                        {titleElement}
                      </Box>
                      {setShow && (
                        <Absolute $right="S" $top="S">
                          <IconButton
                            icon="close"
                            ariaLabel="Close"
                            onClick={() => setShow(false)}
                          />
                        </Absolute>
                      )}
                    </div>
                  )}
                  <div className={clsx(styles.modalContent, "sidebar_block hide_scroll")}>
                    {childrenElement}
                  </div>
                  {bottomElement && (
                    <div
                      className={clsx(styles.bottomElementWrapper, {
                        [styles.center]: type === "center",
                        [styles.side]: type === "side",
                        [styles.bottom]: type === "bottom",
                      })}
                    >
                      {bottomElement}
                    </div>
                  )}
                </div>
              </div>
              {helperElement && <div className={styles.helperWrapper}>{helperElement}</div>}
              {helperButton && (
                <Button
                  $type="third"
                  $color={helperButton.color ?? "white"}
                  $margin="0 auto"
                  onClick={helperButton.onClick}
                >
                  {helperButton.text}
                </Button>
              )}
            </div>
          </>
        </CSSTransition>
      </Portal>
    </>
  )
}

export default Modal
